import {Coa, CoaAccount, ExternalCoa, ExternalCoaAccount, ExternalCoaMapping} from "./types";
import {ReactElement, useEffect, useMemo, useState} from "react";
import {Col, Row} from "@zendeskgarden/react-grid";
import {FinancialEntityType} from "../../../__generated__/generated_types";
import {DnDKitDraggable} from "../../../components/drag-and-drop/DnDKitDraggable";
import {DnDKitDropzone} from "../../../components/drag-and-drop/DnDKitDropzone";
import {DndContext, DragEndEvent, DragStartEvent} from "@dnd-kit/core";
import {Button} from "@zendeskgarden/react-buttons";
import {ReactComponent as TrashIcon} from "@zendeskgarden/svg-icons/src/16/trash-fill.svg";
import {ReactComponent as SearchIcon} from '@zendeskgarden/svg-icons/src/16/search-stroke.svg';
import * as css from "./styles/ExternalChartOfAccountsDnDMapping.module.scss";
import {Field, Label, MediaInput, Toggle} from "@zendeskgarden/react-forms";
import {DnDKitDragOverlay} from "../../../components/drag-and-drop/DnDKitDragOverlay";
import {useMappingUpdater} from "./logic/useMappingUpdater";

type OrderedExternalCoaAccount = ExternalCoaAccount & {order: number};

export interface ExternalChartOfAccountsDnDMappingProps {
    coa: Coa;
    externalCoa: ExternalCoa;
    mapping: ExternalCoaMapping | null;
}

function renderDraggableExternalAccount(externalAccount: OrderedExternalCoaAccount): ReactElement {
    return <DnDKitDraggable key={externalAccount.id} id={externalAccount.id} data={externalAccount}>{externalAccount.name}</DnDKitDraggable>;
}

function renderMappedExternalAccount(externalAccount: OrderedExternalCoaAccount, onRemove: (external: OrderedExternalCoaAccount) => void): ReactElement {
    return <div key={externalAccount.id} className={css.mappedAccount}>
        <Button isDanger isLink onClick={() => onRemove(externalAccount)}>
            <TrashIcon aria-label={`Un-map ${externalAccount.name}`} />
        </Button>
        {externalAccount.name}
    </div>;
}

function renderCoaAccount(coaAccount: CoaAccount, children: OrderedExternalCoaAccount[], onRemove: (coa: CoaAccount, external: OrderedExternalCoaAccount) => void): ReactElement {
    return <div key={coaAccount.id} className={css.coaAccount}>
        <div className={css.accountNumberName}>{coaAccount.glNumber} {coaAccount.glName}</div>
        <DnDKitDropzone id={coaAccount.id} noChildren={<span className={css.noChildren}>Add Account</span>}>
            {children.length > 0 ?
                <div className={css.mappedAccountWrapper}>{children.map(a => renderMappedExternalAccount(a, (external) => onRemove(coaAccount, external)))}</div>
                : null
            }
        </DnDKitDropzone>
    </div>;
}

export function ExternalChartOfAccountsDnDMapping(props: ExternalChartOfAccountsDnDMappingProps): ReactElement {
    const {coa, externalCoa, mapping} = props;
    const {partialUpdate, enableAutoSave, autoSave, lastSaved, saving, save} = useMappingUpdater({
        coa,
        externalCoa
    });

    const filteredCoaAccounts = useMemo(() => {
        return coa.records.filter(r => r.type === FinancialEntityType.Account);
    }, [coa.records]);

    const {orderedExternalAccounts, externalAccountIdMap} = useMemo(() => {
        const ordered = externalCoa.accounts.map((a, i) => ({...a, order: i}));
        const idMap = ordered.toIdMap(a => a.id);

        // You must filter after the idMap is created; otherwise,
        // some external accounts may not get assigned an order,
        // and they won't be included in the map.
        let filtered = ordered;
        if(mapping?.mapping) {
            const mapped = new Set<string>(mapping.mapping.map(m => m.externalCoaAccountId));
            filtered = filtered.filter(a => !mapped.has(a.id));
        }

        return {
            orderedExternalAccounts: filtered,
            externalAccountIdMap: idMap
        };
    }, [externalCoa.accounts, mapping]);

    const existingMapped = useMemo(() => {
        const existing = new Map<string, OrderedExternalCoaAccount[]>();
        if(!mapping) {
            return existing;
        }


        for(const entry of mapping.mapping) {
            const externalAccount = externalAccountIdMap[entry.externalCoaAccountId];
            if(!externalAccount) {
                continue;
            }

            let val = existing.get(entry.accountId);
            if(!val) {
                val = [];
                existing.set(entry.accountId, val);
            }
            val.push(externalAccount);
        }
        return existing;
    }, [externalAccountIdMap, mapping]);

    const [activeDrag, setActiveDrag] = useState<OrderedExternalCoaAccount | null>(null);
    const [externalAccounts, setExternalAccounts] = useState<OrderedExternalCoaAccount[]>(orderedExternalAccounts);
    const [searchedExternalAccounts, setSearchedExternalAccounts] = useState<OrderedExternalCoaAccount[]>(orderedExternalAccounts);
    const [externalAccountSearch, setExternalAccountSearch] = useState<string>("");
    const [internalAccounts, setInternalAccounts] = useState<CoaAccount[]>(filteredCoaAccounts);
    const [internalAccountSearch, setInternalAccountSearch] = useState<string>("");
    const [mappedAccounts, setMappedAccounts] = useState<{map: Map<string, OrderedExternalCoaAccount[]>}>({map: existingMapped});

    useEffect(() => {
        if(internalAccountSearch === "") {
            setInternalAccounts(filteredCoaAccounts);
            return;
        }

        const searched = filteredCoaAccounts.filter(a => {
            let glNumberMatch = false;
            if(a.glNumber) {
                glNumberMatch = a.glNumber.toLowerCase().includes(internalAccountSearch.toLowerCase());
            }
            return a.glName.toLowerCase().includes(internalAccountSearch.toLowerCase()) || glNumberMatch;
        });
        setInternalAccounts(searched);
    }, [internalAccountSearch]);

    useEffect(() => {
        if(externalAccountSearch === "") {
            setSearchedExternalAccounts(externalAccounts);
            return;
        }

        const searched = externalAccounts.filter(a => {
            return a.name.toLowerCase().includes(externalAccountSearch.toLowerCase());
        });
        setSearchedExternalAccounts(searched);
    }, [externalAccountSearch, externalAccounts]);

    function dragEnd(event: DragEndEvent) {
        setActiveDrag(null);
        if(!event.over || typeof event.over.id !== "string" || !event.active.data.current) {
            return;
        }

        const account = event.active.data.current as OrderedExternalCoaAccount;
        const newExternalAccounts = externalAccounts.filter(a => a.id !== event.active.id);

        const map = mappedAccounts.map;
        let valList = map.get(event.over.id);
        if(!valList) {
            valList = [];
            map.set(event.over.id, valList);
        }
        valList.push(account);

        partialUpdate(event.over.id, valList.map(a => a.id));
        setExternalAccounts(newExternalAccounts);
        setMappedAccounts({map: map});
    }

    function dragStart(event: DragStartEvent): void {
        if(event.active.data.current) {
            setActiveDrag(event.active.data.current as OrderedExternalCoaAccount);
        }
    }

    function reinsert(account: OrderedExternalCoaAccount): OrderedExternalCoaAccount[] {
        // Binary search to find index
        let low = 0;
        let high = externalAccounts.length - 1;
        while(low != high) {
            const mid = low + Math.ceil((high - low) / 2);
            const midVal = externalAccounts[mid];
            if(midVal!.order > account.order) {
                high = mid - 1;
            } else {
                low = mid;
            }
        }

        const existing = externalAccounts[low];
        if(existing!.order > account.order) {
            const newAccounts = externalAccounts.slice();
            newAccounts.splice(low, 0, account);
            return newAccounts;
        } else {
            const newAccounts = externalAccounts.slice();
            newAccounts.splice(low + 1, 0, account);
            return newAccounts;
        }
    }

    function unMap(coa: CoaAccount, external: OrderedExternalCoaAccount): void {
        const map = mappedAccounts.map;
        const valList = map.get(coa.id);
        if(!valList) {
            return;
        }
        const newValList = valList.filter(a => a.id !== external.id);
        map.set(coa.id, newValList);
        const newAccounts = reinsert(external);

        partialUpdate(coa.id, newValList.map(a => a.id));
        setMappedAccounts({map: map});
        setExternalAccounts(newAccounts);
    }

    const map = mappedAccounts.map;
    return <DndContext onDragEnd={dragEnd} onDragStart={dragStart}>
        <div className={css.saveButtons}>
            <div>
                Last Saved: {lastSaved ? lastSaved.toLocaleString() : "N/A"}
            </div>
            <div>
                <Field>
                    <Toggle checked={autoSave} onChange={e => enableAutoSave(e.target.checked)}>
                        <Label>Auto Save</Label>
                    </Toggle>
                </Field>
            </div>
            <div>
                <Button isPrimary onClick={save} disabled={saving}>Save</Button>
            </div>
        </div>
        <Row>
            <Col>
                <div className={css.header}>
                    <h3>{externalCoa.name} Accounts</h3>
                    <form>
                        <Field>
                            <Label hidden={true}>Filter External Accounts</Label>
                            <MediaInput
                                type="text"
                                placeholder="Filter External Accounts"
                                start={<SearchIcon />}
                                value={externalAccountSearch}
                                onChange={e => setExternalAccountSearch(e.target.value)}
                            />
                        </Field>
                    </form>
                </div>
                <div className={css.list}>
                    {searchedExternalAccounts.map(a => renderDraggableExternalAccount(a))}
                </div>
            </Col>
            <Col>
                <div className={css.header}>
                    <h3>Internal Accounts</h3>
                    <form>
                        <Field>
                            <Label hidden={true}>Filter Internal Accounts</Label>
                            <MediaInput
                                type="text"
                                placeholder="Filter Internal Accounts"
                                start={<SearchIcon />}
                                value={internalAccountSearch}
                                onChange={e => setInternalAccountSearch(e.target.value)}
                            />
                        </Field>
                    </form>
                </div>
                <div className={css.list}>
                    {internalAccounts.map(a => renderCoaAccount(a, map.get(a.id) || [], unMap))}
                </div>
            </Col>
        </Row>
        <DnDKitDragOverlay>
            {activeDrag ? activeDrag.name : null}
        </DnDKitDragOverlay>
    </DndContext>;
}